import { Box, Button, Grid, makeStyles, Typography } from "@material-ui/core";
import { MeetingRoom } from "@material-ui/icons";
import { SubmitButton } from "@microrealestate/commonui/components";
import { Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import useTranslation from "next-translate/useTranslation";
import { useCallback, useMemo } from "react";

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
  },
  overviewContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    // [theme.breakpoints.down("md")]: {
    //   flexDirection: "column",
    // },
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  legendContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(2),
  },
  legendRectangle: {
    width: 16,
    height: 16,
    marginRight: theme.spacing(1),
    borderRadius: 4,
  },
  overviewCard: {
    padding: theme.spacing(2),
    height: "100%",
    backgroundColor: theme.palette.action.hover,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  outerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  gridContainer: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(10, 1fr)",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(8, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(6, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  },
  active_vacant: {
    backgroundColor: "#38b000",
  },
  active_occupied: {
    backgroundColor: "#e5383b",
  },
  inactive: {
    backgroundColor: "#adb5bd",
  },
  propertySection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  propertyHeading: {
    marginBottom: theme.spacing(1.5),
  },
  toggleButtonlabel: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
  },
}));

function RoomToggleButton({ titleLabel, label, enabled, vacant, onToggle, disabledReason }) {
  const classes = useStyles();
  const isOccupied = !vacant;
  const isInactive = !enabled;

  // Can't disable occupied rooms typically?
  // Or maybe we just show why it's disabled.
  const isDisabled = !!disabledReason;

  return (
    <Button
      type="button"
      variant="outlined"
      fullWidth
      onClick={onToggle}
      disabled={isDisabled && !enabled} // Allow disabling even if limit reached, but prevent enabling
      title={disabledReason || (titleLabel ? `${titleLabel} — ${label}` : label)}
      style={{
        display: "flex",
        padding: 8,
        minHeight: 72,
        borderRadius: 12,
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: isInactive ? "#adb5bd" : isOccupied ? "#e5383b" : "#38b000",
        backgroundColor: isInactive ? "#adb5bd" : "#fff",
        color: isInactive ? "#eee" : isOccupied ? "#e5383b" : "#38b000",
        textTransform: "none",
        flexDirection: "column",
        gap: 4,
        alignItems: "flex-start",
        justifyContent: "center",
        position: "relative",
        opacity: isDisabled && !enabled ? 0.5 : 1,
      }}
      classes={{
        label: classes.toggleButtonlabel,
      }}
    >
      <span style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
        <MeetingRoom fontSize="small" />
        <Typography variant="caption" component="span" style={{ lineHeight: 1.2, fontWeight: 600 }}>
          {label}
        </Typography>
      </span>
      {isDisabled && !enabled && (
        <Typography variant="caption" style={{ fontSize: "0.6rem", color: "#f44336", fontWeight: 700 }}>
          {disabledReason}
        </Typography>
      )}
    </Button>
  );
}

const ManageRoomForm = ({ activeRoomLimit = 0, properties = [], rooms = [], onSubmit }) => {
  const { t } = useTranslation("common");
  const classes = useStyles();

  const initialValues = useMemo(() => {
    return {
      rooms: rooms.map((r) => ({
        id: r.id,
        propertyId: r.propertyId,
        propertyName: r.propertyName || "",
        name: r.name,
        enabled: r.enabled ?? !r.disabled,
        vacant: r.vacant ?? r.room_occupancy === "vacant",
      })),
    };
  }, [rooms]);

  const handleSubmit = useCallback(
    async (values) => {
      if (onSubmit) {
        await onSubmit(values.rooms);
      }
    },
    [onSubmit],
  );

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
      {({ values, setFieldValue, isSubmitting }) => {
        const totalRooms = values.rooms?.length ?? 0;
        const activeCount = values.rooms.filter((r) => r.enabled).length;
        const remainingLimit = activeRoomLimit - activeCount;
        const limitReached = remainingLimit <= 0;

        return (
          <Form autoComplete="off">
            {/* Room Status Overview */}
            <Box mb={3}>
              <Box mb={2} className={classes.overviewContainer}>
                <Box>
                  <Typography variant="subtitle1" component="h2" className={classes.sectionTitle}>
                    {t("Active room status")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("Enable or disable rooms within your purchased limit.")}
                  </Typography>
                </Box>
                <Box className={classes.legendContainer}>
                  <Box className={classes.legendItem}>
                    <Box className={`${classes.legendRectangle} ${classes.active_vacant}`} />
                    <Typography variant="body2">{t("Active Vacant")}</Typography>
                  </Box>
                  <Box className={classes.legendItem}>
                    <Box className={`${classes.legendRectangle} ${classes.active_occupied}`} />
                    <Typography variant="body2">{t("Active Occupied")}</Typography>
                  </Box>
                  <Box className={classes.legendItem}>
                    <Box className={`${classes.legendRectangle} ${classes.inactive}`} />
                    <Typography variant="body2">{t("Inactive")}</Typography>
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box className={classes.overviewCard}>
                    <Typography variant="caption" color="textSecondary" style={{ textTransform: "uppercase" }}>
                      {t("Purchased Rooms")}
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 800 }}>
                      {activeRoomLimit}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className={classes.overviewCard}>
                    <Typography variant="caption" color="textSecondary" style={{ textTransform: "uppercase" }}>
                      {t("Currently Enabled")}
                    </Typography>
                    <Typography variant="h5" style={{ fontWeight: 800, color: limitReached ? "#f44336" : "inherit" }}>
                      {activeCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className={classes.overviewCard}>
                    <Typography variant="caption" color="textSecondary" style={{ textTransform: "uppercase" }}>
                      {t("Available Slot")}
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 800, color: remainingLimit > 0 ? "#38b000" : "inherit" }}
                    >
                      {Math.max(0, remainingLimit)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Room Status List */}
            <Box className={classes.outerContainer}>
              {properties.map((property) => {
                const propertyRooms = values.rooms
                  .map((room, index) => ({ room, index }))
                  .filter(({ room }) => room.propertyId === property.id);

                if (propertyRooms.length === 0) return null;

                return (
                  <Box key={property.id} className={classes.propertySection}>
                    <Box className={classes.propertyHeading}>
                      <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                        {property.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t("{{count}} rooms", { count: propertyRooms.length })}
                      </Typography>
                    </Box>
                    <Box className={classes.gridContainer}>
                      {propertyRooms.map(({ room, index }) => {
                        const canToggleOn = !limitReached || room.enabled;
                        const disabledReason = !room.enabled && limitReached ? t("Limit reached") : null;

                        return (
                          <RoomToggleButton
                            key={room.id}
                            titleLabel={property.name}
                            label={room.name}
                            enabled={room.enabled}
                            vacant={room.vacant}
                            disabledReason={disabledReason}
                            onToggle={() => {
                              if (!room.enabled && limitReached) return;
                              setFieldValue(`rooms.${index}.enabled`, !room.enabled);
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <SubmitButton size="large" label={!isSubmitting ? t("Save Room Status") : t("Saving...")} />
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default observer(ManageRoomForm);
